import React, { useEffect, useState } from "react";
import api from "../lib/apiClient";
import { STATUS_STYLES, CHART_COLORS } from "../lib/statusConfig";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useParams } from "react-router-dom";
import { divisionLabel } from "../lib/divisions";
import { FileText, FolderOpen, CheckCircle2, XCircle } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div data-testid={`stat-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className="border border-border bg-card p-5 rounded-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="font-head text-4xl font-extrabold tracking-tight tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}

function Panel({ title, children, span = "", subtitle }) {
  return (
    <div className={`border border-border bg-card rounded-sm ${span}`}>
      <div className="px-5 py-3.5 border-b border-border flex items-baseline justify-between">
        <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">{title}</h2>
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const makeTooltip = (data, key = "value", noun = "cases") => ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = (data || []).reduce((s, d) => s + (Number(d[key]) || 0), 0);
  const val = Number(payload[0].value) || 0;
  const pct = total ? ((val / total) * 100).toFixed(1) : "0.0";
  const name = label ?? payload[0].name ?? payload[0].payload?.name;
  return (
    <div className="bg-card border border-border rounded-sm px-3 py-2 text-xs shadow-sm">
      <div className="font-medium mb-0.5">{name}</div>
      <div className="tabular-nums">{val.toLocaleString()} {noun} · {pct}%</div>
    </div>
  );
};

const BUCKETS = [
  { value: "all", label: "All Grievances" },
  { value: "local", label: "Local Grievances" },
  { value: "fwd_office", label: "FWD to Office" },
  { value: "terminations", label: "Terminations" },
];

export default function Statistics() {
  const { division = "ramp" } = useParams();
  const [bucket, setBucket] = useState("all");
  const [stats, setStats] = useState(null);
  const { theme } = useTheme();
  const dark = theme === "dark";
  const barInk = dark ? "#E4E4E7" : "#18181B";
  const gridStroke = dark ? "#27272A" : "#E4E4E7";
  const cursorFill = dark ? "#27272A" : "#F4F4F5";

  useEffect(() => {
    setStats(null);
    api.get("/stats", { params: { bucket, division } }).then((r) => setStats(r.data));
  }, [bucket, division]);

  const settledRate = stats && stats.total ? Math.round((stats.settled / stats.total) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">{divisionLabel(division)} · Analytics</p>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">Statistics</h1>
        </div>
        <Select value={bucket} onValueChange={setBucket}>
          <SelectTrigger data-testid="stats-bucket-filter" className="rounded-sm w-[200px] h-10 bg-card"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card">
            {BUCKETS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!stats ? (
        <div className="text-muted-foreground text-sm">Loading statistics…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Kpi label="Total Cases" value={stats.total} icon={FileText} accent="#18181B" />
            <Kpi label="Open" value={stats.open} icon={FolderOpen} accent="#0EA5E9" />
            <Kpi label="Settled" value={stats.settled} icon={CheckCircle2} accent="#22C55E" />
            <Kpi label="Withdrawn" value={stats.withdrawn} icon={XCircle} accent="#6B7280" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <Panel title="Cases by Status" span="lg:col-span-2">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.by_status} margin={{ top: 10, right: 10, left: -6, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
                  <XAxis dataKey="name" angle={-25} textAnchor="end" height={70} interval={0}
                    tick={{ fontSize: 10, fill: "#71717A" }} axisLine={{ stroke: gridStroke }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={makeTooltip(stats.by_status)} cursor={{ fill: cursorFill }} />
                  <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                    {stats.by_status.map((e, i) => (
                      <Cell key={e.name} fill={STATUS_STYLES[e.name]?.dot || barInk} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Case Distribution" subtitle={bucket === "all" ? "by page" : "by discipline"}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={bucket === "all" ? stats.by_bucket : stats.by_discipline} dataKey="value" nameKey="name"
                    innerRadius={60} outerRadius={100} paddingAngle={2} strokeWidth={0}>
                    {(bucket === "all" ? stats.by_bucket : stats.by_discipline).map((e, i) => (
                      <Cell key={e.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={makeTooltip(bucket === "all" ? stats.by_bucket : stats.by_discipline)} />
                  <Legend iconType="square" wrapperStyle={{ fontSize: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <Panel title="Filing Trend" span="lg:col-span-2" subtitle="last 12 months">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={stats.trend} margin={{ top: 10, right: 20, left: -6, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={{ stroke: gridStroke }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={makeTooltip(stats.trend, "count")} />
                  <Line type="monotone" dataKey="count" stroke={barInk} strokeWidth={2}
                    dot={{ r: 3, fill: barInk }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Resolution Rate">
              <div className="flex flex-col items-center justify-center h-[260px]">
                <div className="relative">
                  <ResponsiveContainer width={180} height={180}>
                    <PieChart>
                      <Pie data={[{ name: "Settled", value: stats.settled }, { name: "Other", value: Math.max(0, stats.total - stats.settled) }]}
                        dataKey="value" innerRadius={64} outerRadius={84} startAngle={90} endAngle={-270} strokeWidth={0}>
                        <Cell fill="#22C55E" />
                        <Cell fill="#E4E4E7" />
                      </Pie>
                      <Tooltip content={makeTooltip([{ name: "Settled", value: stats.settled }, { name: "Other", value: Math.max(0, stats.total - stats.settled) }])} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-head text-3xl font-extrabold tabular-nums">{settledRate}%</span>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Settled</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4 text-center">{stats.settled.toLocaleString()} of {stats.total.toLocaleString()} cases settled</p>
              </div>
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <Panel title="Grievances by Quarter">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={stats.by_quarter} margin={{ top: 10, right: 16, left: -6, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
                  <XAxis dataKey="quarter" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={{ stroke: gridStroke }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip content={makeTooltip(stats.by_quarter, "count", "grievances")} cursor={{ fill: cursorFill }} />
                  <Bar dataKey="count" fill={barInk} radius={[2, 2, 0, 0]} barSize={38} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Top Subjects">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart layout="vertical" data={stats.top_subjects} margin={{ top: 0, right: 24, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridStroke} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: "#52525B" }} axisLine={false} tickLine={false} />
                  <Tooltip content={makeTooltip(stats.top_subjects)} cursor={{ fill: cursorFill }} />
                  <Bar dataKey="value" fill="#0EA5E9" radius={[0, 2, 2, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Cases by Rep / Specialist">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart layout="vertical" data={stats.by_rep} margin={{ top: 0, right: 24, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridStroke} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#52525B" }} axisLine={false} tickLine={false} />
                  <Tooltip content={makeTooltip(stats.by_rep)} cursor={{ fill: cursorFill }} />
                  <Bar dataKey="value" fill={barInk} radius={[0, 2, 2, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="By Discipline">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart layout="vertical" data={stats.by_discipline} margin={{ top: 0, right: 24, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridStroke} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#52525B" }} axisLine={false} tickLine={false} />
                  <Tooltip content={makeTooltip(stats.by_discipline)} cursor={{ fill: cursorFill }} />
                  <Bar dataKey="value" fill="#A855F7" radius={[0, 2, 2, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
